import { NgClass, NgStyle } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Optional,
  Output,
  Self,
  ViewEncapsulation,
  computed,
  input
} from '@angular/core';
import { FormsModule, NgControl } from '@angular/forms';

import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  heroCheck,
  heroExclamationCircle,
  heroExclamationTriangle,
  heroInformationCircle,
  heroXMark
} from '@ng-icons/heroicons/outline';
import { NgSelectModule } from '@ng-select/ng-select';

import { BaseSelect, SelectProps } from './base-select';
import { SpinLoaderComponent } from '../../spin-loader/spin-loader.component';

export type LabelIconVariant = 'success' | 'danger' | 'warning' | 'info';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'ui-select',
  standalone: true,
  imports: [
    NgStyle,
    NgSelectModule,
    FormsModule,
    SpinLoaderComponent,
    NgClass,
    NgIconComponent
  ],
  providers: [
    provideIcons({
      heroXMark,
      heroExclamationTriangle,
      heroExclamationCircle,
      heroCheck,
      heroInformationCircle
    })
  ],
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss', './multi-select.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SelectComponent<T> extends BaseSelect<T> implements OnInit {
  // TODO: Remover depois que removermos o bootstrap
  protected readonly accentColor = '#4e2f92';
  readonly iconVariant = input<LabelIconVariant>('success');

  readonly icon = computed(() => {
    if (this.finishedItemArray.length === 0) {
      return '';
    }
    const iconVariant = this.iconVariant();

    const icons = {
      success: 'heroCheck',
      danger: 'heroExclamationCircle',
      warning: 'heroExclamationTriangle',
      info: 'heroInformationCircle'
    };

    return icons[iconVariant];
  });

  readonly completedTextColor = computed(() => {
    if (this.finishedItemArray.length === 0) {
      return '';
    }
    const iconVariant = this.iconVariant();
    const classes = {
      success: 'text-feedback-success-medium',
      danger: 'text-feedback-warning-medium',
      warning: 'text-feedback-helper-medium',
      info: 'text-brand-sextenary-medium'
    };
    return classes[iconVariant];
  });

  @Input() finishedItemArray: Array<boolean>;

  @Input() finishedItemLabel: string;
  /** Base Select Props */

  /** Nome do select */
  @Input() set name(v: string) {
    this._name = v;
  }

  /** Radio da borda do select */
  @Input() set rounded(v: SelectProps['rounded']) {
    if (v !== 'sm' && v !== 'md' && v !== 'lg') {
      throw new Error('Invalid rounded value, please use "sm", "md" or "lg".');
    }
    this._rounded = v;
  }

  @Input() set size(v: SelectProps['rounded']) {
    if (v !== 'sm' && v !== 'md' && v !== 'lg') {
      throw new Error('Invalid size value, please use "sm", "md" or "lg".');
    }
    this._size = v;
  }

  /** Descrição do select */
  @Input() description?: string;

  /** Desabilita o select */
  @Input()
  set disabled(v: boolean) {
    this._disabled = v;
  }

  /** Label do select */
  @Input() set label(label: string) {
    this._label = label;
  }

  @Input() labelColor = '#2d2a34';

  /** Opens dropdown upwards **/
  @Input() invertedDropdown = false;

  /** Others Props */

  /** Placeholder do select */
  @Input()
  placeholder = '';
  /** Valor do select apenas para leitura */
  @Input()
  readonly = false;
  @Input()
  bindValue: string;
  @Input()
  bindLabel: string;
  /** Opções do select */
  @Input()
  options: T[] = [];
  /** Define se o select é limpável */
  @Input()
  clearable = true;
  /** Múltipla seleção */
  @Input()
  multiple = false;
  /** Agrupar opções */
  @Input()
  groupBy: string;
  @Input()
  selectableGroup = false;
  @Input() selectableGroupAsModel = false;
  /** Fecha o select ao selecionar um item */
  @Input()
  closeOnSelect = true;
  /** Exibe um spinner no select */
  @Input()
  isLoading = false;
  /** Se é para vir um valor default setado */
  @Input()
  setDefaultValue = false;
  /** Função de busca */
  @Input()
  searchFn: (term: string, item: T) => boolean;

  @Output() selectChange = this.onchange;

  @Output() openChange = new EventEmitter();

  @Output() closeChange = new EventEmitter();

  ngOnInit(): void {
    if (this.options?.length > 0 && this.setDefaultValue) {
      this.value = this.options[0][this.bindValue];
      this.selectChange.emit(this.value as T[keyof T]);
    }
  }

  get showClearIcon(): boolean {
    return (
      this.clearable &&
      this.value !== null &&
      this.value !== undefined &&
      this.value !== ''
    );
  }

  get hasValue(): boolean {
    if (this.value === null || this.value === undefined || this.value === '') {
      return false;
    }

    if (Array.isArray(this.value)) {
      return this.value.length > 0;
    }

    if (typeof this.value === 'object') {
      return Object.keys(this.value).length > 0;
    }

    return true;
  }

  constructor(@Self() @Optional() protected readonly ngControl: NgControl) {
    super(ngControl);
  }

  select(data: T): void {
    const value = this.optionValue(data);

    this.selectChange.emit(value);
  }

  open(): void {
    this.openChange.emit();
  }

  close(): void {
    this.closeChange.emit();
  }

  optionValue(option: T): any | null {
    return option ? option[this.bindValue] : null;
  }
}
